import type { PhoneChallengeIntent } from '@secretario/shared';
import { track } from '../lib/track.js';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { requestChallenge, verifyChallenge, type RequestedChallenge } from '../lib/api/auth.js';
import { ApiError, describeError } from '../lib/api/http.js';
import { characterImage, DEFAULT_CHARACTER } from '../lib/characters.js';
import { canSuggestDevicePhone, suggestDevicePhone, waitForSmsCode } from '../lib/native.js';
import { hasKnownAccount } from '../lib/session.js';
import { ConsentCheck, DocLink } from '../components/legal/Consent.js';
import {
  GOOGLE_CLIENT_ID,
  googleClaims,
  googleLogin,
  googleRegister,
  loginWithPasskey,
  nativeGoogleAvailable,
  nativeGoogleIdToken,
  passkeysSupported,
  recoverRequest,
  recoverVerify,
  renderGoogleButton,
  totpLogin,
} from '../lib/api/security.js';
import { isNativeShell } from '../lib/platform.js';
import { aiConsentRecord } from '../components/assistant/AiConsentSheet.js';
import { patchProfile } from '../lib/api/profile.js';
import './entrar.css';

/** Versão dos Termos aceita neste aparelho. */
const TERMS_KEY = 'secretario.termos.aceitos';

type Step = 'phone' | 'code' | 'register' | 'recover' | 'totp';

function digitsOnly(value: string) {
  return value.replace(/\D/g, '');
}

function formatBrPhone(digits: string) {
  const d = digits.slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function suggestUsername(name: string) {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '.')
    .replace(/[^a-z0-9._]/g, '')
    .replace(/^[._]+|[._]+$/g, '')
    .slice(0, 32);
}

const LANDING_PHONE_KEY = 'secretario.entrada.celular';

/** Endereço da apresentação (secretarios.net), de onde a pessoa vem com o celular digitado. */
const CAPA_URL = 'https://secretarios.net';

/**
 * O celular digitado na apresentação chega depois do "#" (outro endereço: não dá
 * para usar o armazenamento da página). Lido uma vez e apagado do endereço na hora.
 */
function takeLandingPhone(): string {
  try {
    const match = /(?:^#|&)celular=(\d{10,11})(?:&|$)/.exec(window.location.hash);
    if (match?.[1]) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return match[1];
    }
    const stored = window.sessionStorage.getItem(LANDING_PHONE_KEY) ?? '';
    window.sessionStorage.removeItem(LANDING_PHONE_KEY);
    return digitsOnly(stored).slice(0, 11);
  } catch {
    return '';
  }
}

const HERO: Record<Step, 'simpatico' | 'direto' | 'divertido'> = {
  phone: 'simpatico',
  code: 'direto',
  register: 'divertido',
  recover: 'direto',
  totp: 'direto',
};

export function EntrarPage() {
  const [step, setStep] = useState<Step>('phone');
  const [intent, setIntent] = useState<PhoneChallengeIntent>(() =>
    hasKnownAccount() ? 'login' : 'register',
  );
  // Quem digitou o celular na página de apresentação chega com ele preenchido.
  const [phone, setPhone] = useState(() => takeLandingPhone());
  // Quem já aceitou neste aparelho encontra a caixa marcada.
  const [accepted, setAccepted] = useState(() => {
    try {
      return window.localStorage.getItem(TERMS_KEY) === '2';
    } catch {
      return false;
    }
  });
  const [challenge, setChallenge] = useState<RequestedChallenge | null>(null);
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  async function sendCode(nextIntent: PhoneChallengeIntent) {
    const requested = await requestChallenge(`+55${digitsOnly(phone)}`, nextIntent);
    if (nextIntent === 'register') track('signup_started');
    setIntent(nextIntent);
    setChallenge(requested);
    setCode('');
  }

  async function submitPhone(e: FormEvent) {
    e.preventDefault();
    if (!accepted) {
      setError('Pra continuar, marque que aceita os Termos e a Privacidade.');
      return;
    }
    try {
      window.localStorage.setItem(TERMS_KEY, '2');
    } catch {
      /* sem armazenamento: a caixa volta desmarcada na próxima vez */
    }
    if (digitsOnly(phone).length < 10) {
      setError('Digite o número com DDD.');
      return;
    }
    setBusy(true);
    setError('');
    setNote('');
    try {
      await sendCode(intent);
      setStep('code');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  // No aparelho, o código do SMS entra sozinho (um toque de permissão do sistema).
  const [smsCode, setSmsCode] = useState<{ challengeId: string; code: string } | null>(null);
  const challengeId = challenge?.challengeId;
  useEffect(() => {
    if (step !== 'code' || !challengeId) return;
    let cancelled = false;
    void waitForSmsCode().then((received) => {
      if (!cancelled && received) setSmsCode({ challengeId, code: received });
    });
    return () => {
      cancelled = true;
    };
  }, [step, challengeId]);
  useEffect(() => {
    if (!smsCode || step !== 'code' || smsCode.challengeId !== challengeId || busy) return;
    setSmsCode(null);
    setCode(smsCode.code);
    void confirmCode(smsCode.code);
  }, [smsCode, step, challengeId, busy]);

  // Outras portas: passkey e Google (só no navegador) e o e-mail de recuperação.
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverCode, setRecoverCode] = useState('');
  const [recoverTotp, setRecoverTotp] = useState('');
  const [recoverSent, setRecoverSent] = useState(false);
  const [needTotp, setNeedTotp] = useState(false);
  const googleBox = useRef<HTMLDivElement | null>(null);
  // Google achou a conta pelo e-mail de recuperação e ela tem autenticador: falta o código dele.
  const [googleToken, setGoogleToken] = useState('');
  const [googleTotp, setGoogleTotp] = useState('');
  // Conta Google sem cadastro: o cadastro segue com ela, sem número nem SMS.
  const [googleSignup, setGoogleSignup] = useState('');
  function withGoogle(idToken: string) {
    setBusy(true);
    setError('');
    setNote('');
    googleLogin(idToken)
      .then(() => track('login_completed'))
      .catch((err) => {
        if (err instanceof ApiError && err.code === 'step_up_required') {
          setGoogleToken(idToken);
          setNote(err.message);
        } else if (err instanceof ApiError && err.code === 'google_not_linked') {
          const claims = googleClaims(idToken);
          const name = claims.name.trim();
          setDisplayName(name);
          setUsername(suggestUsername(name || claims.email.split('@')[0] || ''));
          setUsernameTouched(false);
          setGoogleSignup(idToken);
          track('signup_started');
          setStep('register');
        } else setError(describeError(err));
      })
      .finally(() => setBusy(false));
  }
  async function continueWithNativeGoogle() {
    setError('');
    try {
      const idToken = await nativeGoogleIdToken();
      if (idToken) withGoogle(idToken);
    } catch {
      setError(
        'Não consegui abrir as contas Google deste aparelho. Tente de novo ou entre pelo número.',
      );
    }
  }
  useEffect(() => {
    const element = googleBox.current;
    if (step !== 'phone' || !element || !GOOGLE_CLIENT_ID || isNativeShell()) return;
    void renderGoogleButton(element, withGoogle);
  }, [step]);
  async function finishGoogle(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await googleLogin(googleToken, googleTotp.trim());
      track('login_completed');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }
  async function enterWithPasskey() {
    setBusy(true);
    setError('');
    try {
      await loginWithPasskey();
      track('login_completed');
    } catch (err) {
      // Cancelou ou não há passkey salva para este site neste aparelho: o navegador não diz qual.
      if (err instanceof Error && err.name === 'NotAllowedError')
        setError(
          'Nenhuma passkey foi usada. Se você ainda não criou uma, entre pelo número (SMS ou autenticador) e crie em Ajustes › Segurança.',
        );
      else setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }
  const [totpCode, setTotpCode] = useState('');
  async function submitTotp(e: FormEvent) {
    e.preventDefault();
    if (digitsOnly(phone).length < 10) {
      setError('Digite o número com DDD.');
      return;
    }
    if (totpCode.replace(/\s/g, '').length < 6) {
      setError('O código do autenticador tem 6 números.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await totpLogin(digitsOnly(phone), totpCode.trim());
      track('login_completed');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }
  function openTotp() {
    setError('');
    setNote('');
    setTotpCode('');
    setStep('totp');
  }
  async function submitRecover(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (!recoverSent) {
        await recoverRequest(recoverEmail);
        setRecoverSent(true);
        setNote(
          'Se esse for o e-mail de recuperação da sua conta (o que você confirmou em Ajustes › Segurança), o código chega em até um minuto. Não chegou? Veja o spam ou tente o outro e-mail que você usa.',
        );
      } else {
        await recoverVerify(recoverEmail, recoverCode, recoverTotp || undefined);
        track('login_completed');
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'step_up_required') setNeedTotp(true);
      else setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }
  const [phoneHint, setPhoneHint] = useState(false);
  useEffect(() => {
    void canSuggestDevicePhone().then(setPhoneHint);
  }, []);
  async function fillDevicePhone() {
    const suggested = await suggestDevicePhone();
    if (!suggested) return;
    // O sistema devolve no formato internacional (+55…): fica só DDD e número.
    const digits = digitsOnly(suggested).replace(/^55(?=\d{10,11}$)/, '');
    setPhone(digits.slice(0, 11));
    setError('');
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    await confirmCode(code);
  }

  async function confirmCode(typed: string) {
    if (!challenge) return;
    if (typed.length !== 6) {
      setError('O código tem 6 números.');
      return;
    }
    if (intent === 'register') {
      // Aparelho novo de quem já tem conta: o mesmo código entra direto.
      setBusy(true);
      setError('');
      try {
        await verifyChallenge({
          intent: 'login',
          challengeId: challenge.challengeId,
          code: typed,
        });
        return;
      } catch (err) {
        if (err instanceof ApiError && err.code === 'account_not_found') {
          setStep('register');
          return;
        }
        setError(describeError(err));
        return;
      } finally {
        setBusy(false);
      }
    }
    await finish(typed);
  }

  async function submitRegister(e: FormEvent) {
    e.preventDefault();
    if (!displayName.trim()) {
      setError('Como você quer ser chamado?');
      return;
    }
    if (!/^[a-z0-9](?:[a-z0-9._]{1,30}[a-z0-9])?$/.test(username)) {
      setError(
        'O @usuário usa letras minúsculas, números, ponto ou traço baixo, com 3 a 32 caracteres.',
      );
      return;
    }
    if (googleSignup) await finishGoogleSignup();
    else await finish();
  }

  async function finishGoogleSignup() {
    if (!accepted) {
      setError('Pra continuar, marque que aceita os Termos e a Privacidade.');
      return;
    }
    try {
      window.localStorage.setItem(TERMS_KEY, '2');
    } catch {
      // Sem armazenamento: só não lembra a caixa marcada.
    }
    setBusy(true);
    setError('');
    try {
      await googleRegister(googleSignup, { username, displayName: displayName.trim() });
      track('signup_completed');
      await patchProfile({
        preferences: { aiConsent: aiConsentRecord(), webSearch: true },
      }).catch(() => {});
    } catch (err) {
      if (err instanceof ApiError && err.code === 'username_taken')
        setError('Esse @usuário já é de alguém. Tenta outro?');
      else if (err instanceof ApiError && err.code === 'google_invalid') {
        // O token do Google venceu (passou muito tempo nesta tela): pede de novo.
        setGoogleSignup('');
        setStep('phone');
        setError('O Google pediu pra confirmar de novo. Toque em "Continuar com o Google".');
      } else setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  async function finish(typed: string = code) {
    if (!challenge) return;
    setBusy(true);
    setError('');
    try {
      if (intent === 'register') {
        await verifyChallenge({
          intent,
          challengeId: challenge.challengeId,
          code: typed,
          username,
          displayName: displayName.trim(),
        });
        track('signup_completed');
        // Os Termos e a Privacidade aceitos na entrada cobrem a inteligência artificial:
        // o registro fica no perfil (data e versão), e nenhuma folha a mais aparece.
        // Pesquisa na internet já vem ligada (a pessoa desliga em Ajustes › Privacidade).
        await patchProfile({
          preferences: { aiConsent: aiConsentRecord(), webSearch: true },
        }).catch(() => {});
      } else {
        await verifyChallenge({ intent, challengeId: challenge.challengeId, code: typed });
        track('login_completed');
      }
      // A sessão gravada faz o App trocar de tela sozinho.
    } catch (err) {
      if (err instanceof ApiError && err.code === 'account_not_found') {
        // O mesmo código vale para criar a conta: segue para o cadastro.
        setIntent('register');
        setNote('Ainda não tem conta por aqui. Vamos criar a sua.');
        setStep('register');
      } else if (err instanceof ApiError && err.code === 'account_already_exists') {
        // Já tem conta neste número: entra com o mesmo código.
        try {
          setIntent('login');
          await verifyChallenge({
            intent: 'login',
            challengeId: challenge.challengeId,
            code: typed,
          });
        } catch (again) {
          setError(describeError(again));
          setStep('code');
        }
      } else if (err instanceof ApiError && err.code === 'username_taken') {
        setError('Esse @usuário já é de alguém. Tenta outro?');
        setStep('register');
      } else {
        setError(describeError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError('');
    try {
      await sendCode(intent);
      setNote('Enviei outro código.');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="en">
      <div className="en-top">
        <span className="en-ornament en-o1" aria-hidden="true" />
        <span className="en-ornament en-o2" aria-hidden="true" />
        <div className="en-brand">
          <img src="/icon-app.png" alt="" /> Secretário
        </div>
        <div className="en-stage">
          <img key={step} src={characterImage(DEFAULT_CHARACTER, HERO[step])} alt="" />
        </div>
      </div>

      <div className="en-sheet">
        <div className="en-inner">
          {step === 'phone' && (
            <form className="en-form" onSubmit={submitPhone} noValidate>
              <div>
                <div className="en-kicker">Bem-vindo</div>
                <h1>Seu Secretário, do seu jeito.</h1>
                <p className="en-sub">
                  Entre com seu número. Sem senha pra decorar: eu te mando um código.
                </p>
              </div>
              <div className="en-field">
                <label htmlFor="phone">Seu celular</label>
                <div className="en-prefix">
                  <span>+55</span>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder="(11) 99999-9999"
                    value={formatBrPhone(digitsOnly(phone))}
                    onChange={(e) => setPhone(digitsOnly(e.target.value))}
                    autoFocus
                  />
                </div>
                {phoneHint && !phone && (
                  <button
                    className="en-link en-hint"
                    type="button"
                    onClick={() => void fillDevicePhone()}
                  >
                    Usar o número deste aparelho
                  </button>
                )}
              </div>
              <ConsentCheck checked={accepted} onChange={setAccepted}>
                Li e aceito os <DocLink doc="termos">Termos</DocLink> e a{' '}
                <DocLink doc="privacidade">Privacidade</DocLink>, e tenho 18 anos ou mais.
              </ConsentCheck>
              {error && <p className="en-error">{error}</p>}
              <button className="en-cta" type="submit" disabled={busy}>
                {busy ? 'Enviando…' : 'Receber código'}
              </button>
              {(passkeysSupported() ||
                nativeGoogleAvailable() ||
                (GOOGLE_CLIENT_ID && !isNativeShell())) && (
                <div className="en-alt">
                  {nativeGoogleAvailable() && (
                    <button
                      type="button"
                      className="en-alt-btn en-google-native"
                      disabled={busy}
                      onClick={() => void continueWithNativeGoogle()}
                    >
                      <GoogleMark />
                      Continuar com o Google
                    </button>
                  )}
                  {passkeysSupported() && (
                    <button
                      type="button"
                      className="en-alt-btn"
                      disabled={busy}
                      onClick={() => void enterWithPasskey()}
                    >
                      Entrar com passkey
                    </button>
                  )}
                  <div ref={googleBox} className="en-google" />
                </div>
              )}
              {googleToken && (
                <div className="en-field">
                  <label htmlFor="google-totp">Código do autenticador (ou um código reserva)</label>
                  <input
                    id="google-totp"
                    className="code"
                    inputMode="text"
                    autoComplete="one-time-code"
                    maxLength={12}
                    value={googleTotp}
                    onChange={(e) => setGoogleTotp(e.target.value)}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="en-cta"
                    disabled={busy || googleTotp.trim().length < 6}
                    onClick={(e) => void finishGoogle(e as unknown as FormEvent)}
                  >
                    {busy ? 'Conferindo…' : 'Entrar com o Google'}
                  </button>
                </div>
              )}
              <button type="button" className="en-link" onClick={openTotp}>
                Entrar com o código do autenticador
              </button>
              <button
                type="button"
                className="en-link"
                onClick={() => {
                  setError('');
                  setStep('recover');
                }}
              >
                Perdi meu número
              </button>
              <a href={CAPA_URL} className="en-link en-about">
                Ainda não conhece? Veja o que o Secretário faz
              </a>
              <p className="en-foot">
                Seu número fica protegido. Só quem já tem você nos contatos te encontra aqui.
              </p>
            </form>
          )}

          {step === 'recover' && (
            <form className="en-form" onSubmit={submitRecover} noValidate>
              <div>
                <div className="en-kicker">Sem o número</div>
                <h1>Entrar pelo e-mail de recuperação</h1>
                <p className="en-lead">
                  Funciona se você ligou um e-mail em Ajustes › Segurança. Mando um código pra ele.
                </p>
              </div>
              <div className="en-field">
                <label htmlFor="recover-email">Seu e-mail</label>
                <input
                  id="recover-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={recoverEmail}
                  onChange={(e) => setRecoverEmail(e.target.value)}
                  disabled={recoverSent}
                  autoFocus
                />
              </div>
              {recoverSent && (
                <div className="en-field">
                  <label htmlFor="recover-code">Código do e-mail</label>
                  <input
                    id="recover-code"
                    className="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={recoverCode}
                    onChange={(e) => setRecoverCode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              )}
              {needTotp && (
                <div className="en-field">
                  <label htmlFor="recover-totp">
                    Código do autenticador (ou um código reserva)
                  </label>
                  <input
                    id="recover-totp"
                    className="code"
                    inputMode="text"
                    autoComplete="one-time-code"
                    maxLength={12}
                    value={recoverTotp}
                    onChange={(e) => setRecoverTotp(e.target.value)}
                  />
                </div>
              )}
              {note && <p className="en-note">{note}</p>}
              {error && <p className="en-error">{error}</p>}
              <button className="en-cta" type="submit" disabled={busy}>
                {busy ? 'Um instante…' : recoverSent ? 'Entrar' : 'Mandar código'}
              </button>
              <button
                type="button"
                className="en-link"
                onClick={() => {
                  setStep('phone');
                  setRecoverSent(false);
                  setNeedTotp(false);
                  setError('');
                }}
              >
                Voltar
              </button>
            </form>
          )}

          {step === 'totp' && (
            <form className="en-form" onSubmit={submitTotp} noValidate>
              <div>
                <div className="en-kicker">Sem SMS</div>
                <h1>Entrar com o autenticador</h1>
                <p className="en-lead">
                  Para quem ligou o autenticador (Google Authenticator, Microsoft, 1Password…) em
                  Ajustes › Segurança. Vale também um código reserva.
                </p>
              </div>
              <div className="en-field">
                <label htmlFor="totp-phone">Seu celular</label>
                <input
                  id="totp-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="(11) 91234-5678"
                  value={formatBrPhone(digitsOnly(phone))}
                  onChange={(e) => setPhone(digitsOnly(e.target.value))}
                  autoFocus={!phone}
                />
              </div>
              <div className="en-field">
                <label htmlFor="totp-code">Código do autenticador</label>
                <input
                  id="totp-code"
                  className="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={12}
                  placeholder="••••••"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  autoFocus={!!phone}
                />
                <span className="hint">
                  O código muda a cada 30 segundos: use o que aparece agora.
                </span>
              </div>
              {error && <p className="en-error">{error}</p>}
              <button className="en-cta" type="submit" disabled={busy}>
                {busy ? 'Conferindo…' : 'Entrar'}
              </button>
              <button
                type="button"
                className="en-link"
                onClick={() => {
                  setStep('phone');
                  setError('');
                }}
              >
                Voltar
              </button>
            </form>
          )}

          {step === 'code' && challenge && (
            <form className="en-form" onSubmit={submitCode} noValidate>
              <div>
                <div className="en-kicker">Quase lá</div>
                <h1>Chegou um código?</h1>
                <p className="en-sub">
                  Enviei pro número terminado em {challenge.delivery.destinationHint.slice(-4)}.
                  Vale por 10 minutos.
                </p>
              </div>
              {note && <p className="en-note">{note}</p>}
              <div className="en-field">
                <label htmlFor="code">Código de 6 números</label>
                <input
                  id="code"
                  className="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => setCode(digitsOnly(e.target.value).slice(0, 6))}
                  autoFocus
                />
              </div>
              {error && <p className="en-error">{error}</p>}
              <button className="en-cta" type="submit" disabled={busy}>
                {busy ? 'Conferindo…' : intent === 'register' ? 'Continuar' : 'Entrar'}
              </button>
              <button className="en-link" type="button" onClick={resend} disabled={busy}>
                Não chegou? Pedir outro código
              </button>
              <button className="en-link" type="button" onClick={openTotp} disabled={busy}>
                Usar o código do autenticador
              </button>
              <button
                className="en-link"
                type="button"
                onClick={() => {
                  setStep('phone');
                  setError('');
                  setNote('');
                }}
              >
                Trocar o número
              </button>
            </form>
          )}

          {step === 'register' && (
            <form className="en-form" onSubmit={submitRegister} noValidate>
              <div>
                <div className="en-kicker">Prazer</div>
                <h1>Como você quer ser chamado?</h1>
                <p className="en-sub">
                  {googleSignup
                    ? 'Sua conta Google confirma quem você é, sem SMS. Confira seu nome e pronto.'
                    : 'É assim que eu e seus contatos vão te encontrar.'}
                </p>
              </div>
              <div className="en-field">
                <label htmlFor="name">Seu nome</label>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  maxLength={80}
                  placeholder="Ex.: Ana Souza"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    if (!usernameTouched) setUsername(suggestUsername(e.target.value));
                  }}
                  autoFocus
                />
              </div>
              <div className="en-field">
                <label htmlFor="username">Seu @usuário</label>
                <input
                  id="username"
                  type="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  maxLength={32}
                  placeholder="ana.souza"
                  value={username}
                  onChange={(e) => {
                    setUsernameTouched(true);
                    setUsername(suggestUsername(e.target.value.replace(/\s+/g, '.')));
                  }}
                />
                <span className="hint">Letras minúsculas, números, ponto ou traço baixo.</span>
              </div>
              {googleSignup && (
                <ConsentCheck checked={accepted} onChange={setAccepted}>
                  Li e aceito os <DocLink doc="termos">Termos</DocLink> e a{' '}
                  <DocLink doc="privacidade">Privacidade</DocLink>, e tenho 18 anos ou mais.
                </ConsentCheck>
              )}
              {error && <p className="en-error">{error}</p>}
              <button className="en-cta" type="submit" disabled={busy}>
                {busy ? 'Criando…' : 'Criar minha conta'}
              </button>
              {googleSignup && (
                <button
                  type="button"
                  className="en-link"
                  onClick={() => {
                    setGoogleSignup('');
                    setError('');
                    setStep('phone');
                  }}
                >
                  Prefiro entrar pelo número
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/** O "G" do Google, nas cores dele (como a marca pede no botão de entrar). */
function GoogleMark() {
  return (
    <svg className="en-google-mark" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
